import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/notes/MarkdownEditor';
import { AssociationSelector } from '@/components/notes/AssociationSelector';
import { FlightLogSelector } from '@/components/notes/FlightLogSelector';
import { PhaseNoteForm } from '@/components/notes/PhaseNoteForm';
import { AIOptimizePanel } from '@/components/notes/AIOptimizePanel';
import { notesApi } from '@/services/notesApi';
import { aiApi } from '@/services/aiApi';
import { useToast } from '@/components/ui/use-toast';
import type { NoteCreateRequest, NotePhase } from '@/types/notes.types';
import type { AINoteSuggestion } from '@/types/ai.types';

const PHASE_OPTIONS: { value: NotePhase; label: string }[] = [
  { value: 'takeoff', label: '起飞' },
  { value: 'cruise', label: '巡航/航线' },
  { value: 'landing', label: '落地' },
  { value: 'crew', label: '机组' },
  { value: 'general', label: '通用' },
];

export default function NoteEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const isEdit = Boolean(id);

  const flightLogParam = searchParams.get('flightLog');
  const flightLogIdFromUrl = flightLogParam ? Number(flightLogParam) : null;
  const isPhaseMode = !isEdit && flightLogIdFromUrl !== null;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [routeId, setRouteId] = useState<number | null>(null);
  const [departureAirportCode, setDepartureAirportCode] = useState<string | null>(null);
  const [arrivalAirportCode, setArrivalAirportCode] = useState<string | null>(null);
  const [crewIds, setCrewIds] = useState<number[]>([]);
  const [flightLogId, setFlightLogId] = useState<number | null>(flightLogIdFromUrl);
  const [flightLegId, setFlightLegId] = useState<number | null>(null);
  const [phase, setPhase] = useState<NotePhase>('general');
  const [aiSuggested, setAiSuggested] = useState(false);
  const [aiSuggestionConfidence, setAiSuggestionConfidence] = useState<number | null>(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<AINoteSuggestion | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(false);

  useEffect(() => {
    aiApi
      .getUserAIConfig()
      .then((res) => setAiEnabled(res.data.data.isEnabled && res.data.data.hasApiKey))
      .catch(() => setAiEnabled(false));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    notesApi
      .getNote(Number(id))
      .then((res) => {
        const note = res.data.data;
        setTitle(note.title);
        setContent(note.content);
        setTags(note.tags || []);
        setRouteId(note.route?.id ?? null);
        setDepartureAirportCode(note.departureAirport?.iataCode ?? null);
        setArrivalAirportCode(note.arrivalAirport?.iataCode ?? null);
        setCrewIds(note.crewMembers?.map((c) => c.id) || []);
        setFlightLogId(note.flightLog ?? null);
        setFlightLegId(note.flightLeg ?? null);
        setPhase(note.phase || 'general');
        setAiSuggested(note.aiSuggested);
        setAiSuggestionConfidence(note.aiSuggestionConfidence);
      })
      .catch(() => {
        toast({ title: '获取笔记失败', variant: 'destructive' });
        navigate('/notes');
      })
      .finally(() => setLoading(false));
  }, [id, isEdit, navigate, toast]);

  const handleAddTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (t: string) => {
    setTags(tags.filter((x) => x !== t));
  };

  const handleRequestAnalyze = async () => {
    if (!content.trim() && !title.trim()) {
      toast({ title: '请先输入标题或内容', variant: 'destructive' });
      return;
    }
    setAiLoading(true);
    try {
      const res = await aiApi.analyzeNote({
        title,
        content,
        flightLog: flightLogId,
        flightLeg: flightLegId,
      });
      setAiSuggestion(res.data.data);
      toast({ title: 'AI 分析完成' });
    } catch {
      toast({ title: 'AI 分析失败', variant: 'destructive' });
    } finally {
      setAiLoading(false);
    }
  };

  const handleApplySuggestion = (suggestion: AINoteSuggestion) => {
    if (suggestion.suggestedTitle) setTitle(suggestion.suggestedTitle);
    if (suggestion.suggestedTags.length > 0) setTags(suggestion.suggestedTags);
    if (suggestion.suggestedPhase) setPhase(suggestion.suggestedPhase);
    if (suggestion.suggestedRouteId !== null) setRouteId(suggestion.suggestedRouteId);
    if (suggestion.suggestedDepartureAirportCode)
      setDepartureAirportCode(suggestion.suggestedDepartureAirportCode);
    if (suggestion.suggestedArrivalAirportCode)
      setArrivalAirportCode(suggestion.suggestedArrivalAirportCode);
    if (suggestion.suggestedCrewIds.length > 0) setCrewIds(suggestion.suggestedCrewIds);
    setAiSuggested(true);
    setAiSuggestionConfidence(suggestion.confidence);
    setAiSuggestion(null);
    toast({ title: '已应用 AI 建议' });
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: '请输入标题', variant: 'destructive' });
      return;
    }

    const payload: NoteCreateRequest = {
      title: title.trim(),
      content,
      tags,
      routeId,
      departureAirportCode,
      arrivalAirportCode,
      crewIds,
      flightLog: flightLogId,
      flightLeg: flightLegId,
      phase,
      aiSuggested,
      aiSuggestionConfidence,
    };

    setSaving(true);
    try {
      if (isEdit) {
        await notesApi.updateNote(Number(id), payload);
        toast({ title: '笔记已更新' });
      } else {
        const res = await notesApi.createNote(payload);
        toast({ title: '笔记已创建' });
        navigate(`/notes/${res.data.data.id}`);
        return;
      }
      navigate('/notes');
    } catch (err: any) {
      const msg = err?.response?.data?.message || (isEdit ? '更新失败' : '创建失败');
      toast({ title: msg, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (isPhaseMode) {
    return (
      <PhaseNoteForm
        flightLogId={flightLogIdFromUrl}
        flightLegId={flightLegId ?? undefined}
        onSaved={() => navigate('/notes')}
        onCancel={() => navigate('/notes')}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* 顶部栏 */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/notes')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-sm font-medium">返回列表</span>
        </button>
        <Button onClick={handleSubmit} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? '保存更新' : '创建笔记'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* 主编辑区 */}
        <div className="lg:col-span-2 space-y-4">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="笔记标题..."
            className="text-lg font-semibold"
          />
          <MarkdownEditor value={content} onChange={setContent} placeholder="开始写下你的笔记..." />

          {/* 标签 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4 inline mr-1" />
              标签
            </label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded cursor-pointer hover:bg-gray-200"
                  onClick={() => handleRemoveTag(t)}
                >
                  {t}
                  <span className="text-gray-400">×</span>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="添加标签..."
                className="text-sm"
              />
              <Button type="button" variant="outline" size="sm" onClick={handleAddTag}>
                添加
              </Button>
            </div>
          </div>
        </div>

        {/* 侧边栏 */}
        <div className="space-y-6">
          {/* 维度 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">笔记维度</label>
            <select
              value={phase}
              onChange={(e) => setPhase(e.target.value as NotePhase)}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {PHASE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* 飞行记录关联 */}
          <FlightLogSelector
            selectedFlightLogId={flightLogId}
            onFlightLogChange={setFlightLogId}
            disabled={flightLogId !== null}
          />

          {/* 关联选择 */}
          <AssociationSelector
            phase={phase}
            selectedRouteId={routeId}
            onRouteChange={setRouteId}
            selectedDepartureAirportCode={departureAirportCode}
            onDepartureAirportChange={setDepartureAirportCode}
            selectedArrivalAirportCode={arrivalAirportCode}
            onArrivalAirportChange={setArrivalAirportCode}
            selectedCrewIds={crewIds}
            onCrewChange={setCrewIds}
          />

          {aiEnabled && (
            <AIOptimizePanel
              suggestion={aiSuggestion}
              loading={aiLoading}
              onApply={handleApplySuggestion}
              onDiscard={() => setAiSuggestion(null)}
              onRequestAnalyze={handleRequestAnalyze}
            />
          )}

          {!aiEnabled && (
            <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-500 flex items-start gap-2">
              <span>在设置中配置 AI API Key 后，可使用 AI 自动优化标题、标签和关联维度。</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
